import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './TerminalDashboard.module.css';
import DataFallback from '../components/feedback/DataFallback';
import { classifyError } from '../utils/errorTaxonomy';
import { resilientFetch, TIMEOUT_NORMAL } from '../utils/resilientFetch';
import { getCurrentUserRole } from '../services/shipturtleApi';
import TerminalBannerManager from '../components/TerminalBannerManager';
import { useTaraTerminalFocus } from '../utils/taraTerminalContext';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─── CSV Export ───────────────────────────────────────────────────────────────

function exportCSV(filename, rows, kpis) {
  const esc = s => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const cell = (val, style = '') => `<td style="padding:6px 10px;border:1px solid #e2e8f0;${style}">${esc(val)}</td>`;

  let html = `<html><head><meta charset="UTF-8"/></head><body>
<table border="0" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-family:Arial,sans-serif;font-size:12px;">`;

  // ── Summary section ──
  if (kpis && kpis.length) {
    html += `<tr><td colspan="2" style="background:#0d9488;color:#fff;font-weight:700;font-size:13px;padding:8px 10px;border:1px solid #0d9488;letter-spacing:0.05em;">SUMMARY STATISTICS</td></tr>`;
    html += `<tr>${cell('Metric','background:#e0f7f5;font-weight:700;color:#0d9488;')}${cell('Value','background:#e0f7f5;font-weight:700;color:#0d9488;')}</tr>`;
    kpis.forEach(k => {
      html += `<tr>${cell(k.label,'color:#374151;')}${cell(k.value,'color:#1e293b;font-weight:600;')}</tr>`;
    });
    html += `<tr><td colspan="2" style="padding:6px;border:none;"></td></tr>`;
  }

  // ── Detail section ──
  const dataRows = rows && rows.length ? rows : null;
  if (dataRows) {
    const headers = Object.keys(dataRows[0]);
    html += `<tr><td colspan="${headers.length}" style="background:#1e293b;color:#fff;font-weight:700;font-size:13px;padding:8px 10px;border:1px solid #1e293b;letter-spacing:0.05em;">DETAIL DATA</td></tr>`;
    html += `<tr>${headers.map(h => cell(h.replace(/_/g, ' ').toUpperCase(), 'background:#1e293b;color:#fff;font-weight:700;white-space:nowrap;')).join('')}</tr>`;
    dataRows.forEach((r, i) => {
      const bg = i % 2 === 0 ? '#ffffff' : '#f8fafc';
      html += `<tr>${headers.map(h => cell(r[h] ?? '', `background:${bg};color:#374151;`)).join('')}</tr>`;
    });
  } else {
    html += `<tr><td style="padding:10px;color:#94a3b8;font-style:italic;border:1px solid #e2e8f0;">No records found for the selected date range</td></tr>`;
  }

  html += `</table></body></html>`;

  const a = document.createElement('a');
  a.href = 'data:application/vnd.ms-excel;charset=utf-8,' + encodeURIComponent(html);
  a.download = filename.replace(/\.csv$/, '.xls');
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function formatINR(val) {
  if (val == null || isNaN(val)) return '—';
  return '₹' + Number(val).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

function toISODate(d) {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseISODate(value) {
  const d = new Date(value);
  return !isNaN(d) ? startOfDay(d) : null;
}

function addDays(d, n) {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function startOfDay(d) {
  const r = new Date(d);
  r.setHours(0, 0, 0, 0);
  return r;
}

function today() { return startOfDay(new Date()); }

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_NAMES   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// ─── Date Presets ─────────────────────────────────────────────────────────────

function getPresetRange(key) {
  const t = today();
  switch (key) {
    case 'today':       return { start: t, end: t };
    case 'yesterday':   return { start: addDays(t,-1), end: addDays(t,-1) };
    case 'last7':       return { start: addDays(t,-6), end: t };
    case 'last30':      return { start: addDays(t,-29), end: t };
    case 'last90':      return { start: addDays(t,-89), end: t };
    case 'last180':     return { start: addDays(t,-179), end: t };
    case 'last365':     return { start: addDays(t,-364), end: t };
    case 'thisMonth': {
      const s = new Date(t.getFullYear(), t.getMonth(), 1);
      return { start: s, end: t };
    }
    case 'lastMonth': {
      const s = new Date(t.getFullYear(), t.getMonth()-1, 1);
      const e = new Date(t.getFullYear(), t.getMonth(), 0);
      return { start: s, end: e };
    }
    case 'all':         return { start: new Date('2020-01-01'), end: t };
    default:            return { start: addDays(t,-29), end: t };
  }
}

const PRESETS = [
  { key: 'today',     label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7',     label: 'Last 7 days' },
  { key: 'last30',    label: 'Last 30 days' },
  { key: 'last90',    label: 'Last 90 days' },
  { key: 'last180',   label: 'Last 180 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'last365',   label: 'Last 365 days' },
  { key: 'all',       label: 'All time' },
  { key: 'custom',    label: 'Custom range' },
];

// ─── Mini Calendar ────────────────────────────────────────────────────────────

function MiniCalendar({ year, month, onDayClick, onDayHover, rangeStart, rangeEnd, hovered }) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  function inRange(d) {
    if (!d) return false;
    const s = rangeStart, e = rangeEnd || hovered;
    if (!s) return false;
    const lo = s <= (e || s) ? s : (e || s);
    const hi = s <= (e || s) ? (e || s) : s;
    return d >= lo && d <= hi;
  }
  function isStart(d) { if (!d || !rangeStart) return false; return toISODate(d) === toISODate(rangeStart); }
  function isEnd(d) {
    if (!d) return false;
    const e = rangeEnd || (hovered && rangeStart ? hovered : null);
    return e && toISODate(d) === toISODate(e);
  }

  return (
    <div className={styles.miniCal}>
      <div className={styles.calDayNames}>{DAY_NAMES.map(n => <span key={n}>{n}</span>)}</div>
      <div className={styles.calGrid}>
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const cls = [styles.calDay, isStart(d) ? styles.calStart : '', isEnd(d) ? styles.calEnd : '', inRange(d) && !isStart(d) && !isEnd(d) ? styles.calInRange : ''].filter(Boolean).join(' ');
          return <span key={i} className={cls} onClick={() => onDayClick(d)} onMouseEnter={() => onDayHover(d)}>{d.getDate()}</span>;
        })}
      </div>
    </div>
  );
}

// ─── Date Picker Dropdown ──────────────────────────────────────────────────────

function DateFilterDropdown({ startDate, endDate, onApply, onClose }) {
  const [activePreset, setActivePreset] = useState('custom');
  const [rangeStart, setRangeStart] = useState(startDate ? parseISODate(startDate) : null);
  const [rangeEnd, setRangeEnd]     = useState(endDate   ? parseISODate(endDate)   : null);
  const [hovered, setHovered]       = useState(null);
  const [picking, setPicking]       = useState(null);
  const [leftYear,  setLeftYear]    = useState(rangeStart ? rangeStart.getFullYear() : today().getFullYear());
  const [leftMonth, setLeftMonth]   = useState(rangeStart ? rangeStart.getMonth()    : today().getMonth());

  const rightYear  = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;
  const inputFmt   = (d) => d ? toISODate(d) : '';

  function selectPreset(key) {
    setActivePreset(key);
    if (key === 'custom') return;
    const { start, end } = getPresetRange(key);
    setRangeStart(startOfDay(start));
    setRangeEnd(startOfDay(end));
    setLeftYear(start.getFullYear()); setLeftMonth(start.getMonth());
    setPicking(null);
  }

  function handleDayClick(d) {
    if (!rangeStart || picking === 'start' || (rangeStart && rangeEnd)) {
      setRangeStart(startOfDay(d)); setRangeEnd(null); setPicking('end'); setActivePreset('custom');
    } else {
      if (d < rangeStart) { setRangeEnd(rangeStart); setRangeStart(startOfDay(d)); }
      else setRangeEnd(startOfDay(d));
      setPicking(null); setActivePreset('custom');
    }
  }

  function prevMonth() { if (leftMonth === 0) { setLeftYear(y => y-1); setLeftMonth(11); } else setLeftMonth(m => m-1); }
  function nextMonth() { if (leftMonth === 11) { setLeftYear(y => y+1); setLeftMonth(0); } else setLeftMonth(m => m+1); }
  function handleApply() {
    if (!rangeStart) return;
    const start = startOfDay(rangeStart);
    const end = rangeEnd ? startOfDay(rangeEnd) : start;
    onApply(toISODate(start), toISODate(end));
  }
  function handleStartInput(e) {
    const d = parseISODate(e.target.value);
    if (d) { setRangeStart(d); setActivePreset('custom'); }
  }
  function handleEndInput(e) {
    const d = parseISODate(e.target.value);
    if (d) { setRangeEnd(d); setActivePreset('custom'); }
  }

  return (
    <div className={styles.dpDropdown} onMouseDown={e => e.stopPropagation()}>
      <div className={styles.dpPresets}>
        {PRESETS.map(p => (
          <button key={p.key} className={`${styles.dpPresetBtn} ${activePreset === p.key ? styles.dpPresetActive : ''}`} onClick={() => selectPreset(p.key)}>{p.label}</button>
        ))}
      </div>
      <div className={styles.dpCalArea}>
        <div className={styles.dpInputRow}>
          <input type="date" className={`${styles.dpInput} ${picking === 'start' ? styles.dpInputActive : ''}`} value={inputFmt(rangeStart)} onChange={handleStartInput} onClick={() => setPicking('start')} />
          <span className={styles.dpArrow}>→</span>
          <input type="date" className={`${styles.dpInput} ${picking === 'end' ? styles.dpInputActive : ''}`} value={inputFmt(rangeEnd)} onChange={handleEndInput} onClick={() => setPicking('end')} />
        </div>
        <div className={styles.dpCalsRow}>
          <div className={styles.dpCalBlock}>
            <div className={styles.calNav}>
              <button className={styles.calNavBtn} onClick={prevMonth}>‹</button>
              <span className={styles.calMonthLabel}>{MONTH_NAMES[leftMonth]} {leftYear}</span>
              <button className={styles.calNavBtn} onClick={nextMonth}>›</button>
            </div>
            <MiniCalendar year={leftYear} month={leftMonth} rangeStart={rangeStart} rangeEnd={rangeEnd} hovered={hovered} onDayClick={handleDayClick} onDayHover={d => picking === 'end' && setHovered(d)} />
          </div>
          <div className={styles.dpCalBlock}>
            <div className={styles.calNav}><span className={styles.calMonthLabel}>{MONTH_NAMES[rightMonth]} {rightYear}</span></div>
            <MiniCalendar year={rightYear} month={rightMonth} rangeStart={rangeStart} rangeEnd={rangeEnd} hovered={hovered} onDayClick={handleDayClick} onDayHover={d => picking === 'end' && setHovered(d)} />
          </div>
        </div>
        <div className={styles.dpActions}>
          <button className={styles.dpCancel} onClick={onClose}>Cancel</button>
          <button className={styles.dpApply} disabled={!rangeStart} onClick={handleApply}>Apply</button>
        </div>
      </div>
    </div>
  );
}

// ─── Date Filter Trigger ───────────────────────────────────────────────────────

function DateFilter({ startDate, endDate, onChange }) {
  const [open, setOpen] = useState(false);
  const [dropTop, setDropTop] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    function close(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  function handleOpen() {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropTop(rect.bottom + 8);
    }
    setOpen(o => !o);
  }

  const label = startDate && endDate ? (startDate === endDate ? startDate : `${startDate} – ${endDate}`) : 'Select date range';

  return (
    <div className={styles.dfWrap} ref={ref}>
      <button className={styles.dfTrigger} onClick={handleOpen}>
        <span className={styles.dfCalIcon}>
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
            <rect x="1" y="2" width="13" height="12" rx="2" stroke="#475569" strokeWidth="1.3"/>
            <path d="M1 6h13" stroke="#475569" strokeWidth="1.3"/>
            <path d="M5 1v2M10 1v2" stroke="#475569" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
        </span>
        {label}
        <span className={styles.dfChevron}>▾</span>
      </button>
      {open && (
        <div style={{ position: 'fixed', top: dropTop, right: '1.5rem', zIndex: 9999 }}>
          <DateFilterDropdown startDate={startDate} endDate={endDate} onApply={(s,e) => { onChange(s,e); setOpen(false); }} onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}

// ─── Shared UI Primitives ──────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statLabel}>{label}</div>
      <div className={`${styles.statValue} ${accent ? styles.accent : ''}`}>{value}</div>
      {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
  );
}

function SectionTitle({ children }) {
  return <h2 className={styles.sectionTitle}>{children}</h2>;
}

// Renders the shipment tracking link the backend provides as a "Track" anchor,
// or an em dash when there's no link. The value is the tracking URL captured
// from the vendor's Shopify fulfillment, so we display it as-is.
function TrackingCell({ url }) {
  return (
    <td>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--primary-orange, #f97316)', textDecoration: 'underline' }}
        >
          Track
        </a>
      ) : (
        '—'
      )}
    </td>
  );
}

function WaterfallRow({ label, value, sign }) {
  const isNeg = sign === '-';
  return (
    <div className={`${styles.waterfallRow} ${isNeg ? styles.waterfallNeg : ''}`}>
      <span>{label}</span>
      <span>{isNeg ? '−' : ''}{formatINR(Math.abs(value))}</span>
    </div>
  );
}

// Both helpers delegate to the shared components, so all eleven dashboard tabs
// pick up the same copy and retry affordance from one place. The props are
// unchanged, so no call site needed editing.
function LoadingState() {
  return (
    <div className={styles.loadingOverlay}>
      <div className={styles.spinner} />
      <p>Loading…</p>
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  // `message` is a classified description now, but older callers may still pass
  // a plain string.
  const error = typeof message === 'string'
    ? { message, retryable: true }
    : message;
  return <DataFallback variant="card" error={error} onRetry={onRetry} />;
}

function TabHeader({ startDate, endDate, defaultRange, onDateChange, onRefresh, onExport, userRole }) {
  const defaultStr    = toISODate(defaultRange.start);
  const defaultEndStr = toISODate(defaultRange.end);
  return (
    <div className={styles.pageHeader}>
      <div className={styles.headerControls}>
        <DateFilter startDate={startDate} endDate={endDate} onChange={onDateChange} />
        {(startDate !== defaultStr || endDate !== defaultEndStr) && (
          <button className={styles.clearFilterBtn} onClick={() => onDateChange(defaultStr, defaultEndStr)}>
            ✕ Clear filter
          </button>
        )}
        <button className={styles.refreshBtn} onClick={onRefresh}>↻ Refresh</button>
        {userRole === 'admin' && onExport && (
          <button className={styles.exportBtn} onClick={onExport}>⬇ Export CSV</button>
        )}
      </div>
    </div>
  );
}

function AgeingBuckets({ buckets }) {
  const colors = ['#22c55e','#84cc16','#eab308','#f97316','#ef4444'];
  const max = Math.max(...(buckets || []).map(b => b.count), 1);
  return (
    <div>
      {(buckets || []).map((b, i) => {
        const pct = max ? (b.count / max) * 100 : 0;
        return (
          <div key={b.label} className={styles.bucketRow}>
            <span className={styles.bucketLabel}>{b.label}</span>
            <div className={styles.bucketTrack}>
              <div className={styles.bucketFill} style={{ width: `${pct}%`, background: colors[Math.min(i, colors.length-1)] }} />
            </div>
            <span className={styles.bucketCount}>{b.count}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function usePagination(items, pageSize = 25) {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil((items?.length || 0) / pageSize);
  const paged = (items || []).slice((page - 1) * pageSize, page * pageSize);
  // Reset to page 1 when items change
  const reset = useCallback(() => setPage(1), []);
  return { paged, page, setPage, totalPages, reset };
}

function PaginationBar({ page, totalPages, setPage, total, pageSize }) {
  if (totalPages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className={styles.paginationBar}>
      <span className={styles.paginationInfo}>{from}–{to} of {total}</span>
      <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(1)}>«</button>
      <button className={styles.pageBtn} disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
      {Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
        .reduce((acc, p, idx, arr) => {
          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
          acc.push(p);
          return acc;
        }, [])
        .map((p, i) => typeof p === 'string'
          ? <span key={`e${i}`} className={styles.pageEllipsis}>{p}</span>
          : <button key={p} className={`${styles.pageBtn} ${p === page ? styles.pageBtnActive : ''}`} onClick={() => setPage(p)}>{p}</button>
        )
      }
      <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
      <button className={styles.pageBtn} disabled={page === totalPages} onClick={() => setPage(totalPages)}>»</button>
    </div>
  );
}

// ─── Donut Chart ───────────────────────────────────────────────────────────────

function DonutChart({ slices, size = 190, hideLegend = false }) {
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  function handleEnter(i, e) { setHovered(i); setMousePos({ x: e.clientX, y: e.clientY }); }
  function handleMove(e) { setMousePos({ x: e.clientX, y: e.clientY }); }
  function handleLeave() { setHovered(null); }

  const total = slices.reduce((s, d) => s + d.value, 0);
  if (!total) return <div className={styles.donutEmpty}>No data</div>;

  let cum = 0;
  const cx = size / 2, cy = size / 2;
  const r = size * 0.32, sw = size * 0.18;
  const fs = Math.round(size * 0.09);

  const paths = slices.map((sl) => {
    const startA = (cum / total) * 2 * Math.PI - Math.PI / 2;
    cum += sl.value;
    const endA = (cum / total) * 2 * Math.PI - Math.PI / 2;
    if (Math.abs(endA - startA) >= 2 * Math.PI - 0.001) {
      return { d: `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx - 0.001} ${cy - r}`, ...sl };
    }
    const large = endA - startA > Math.PI ? 1 : 0;
    const x1 = cx + r * Math.cos(startA), y1 = cy + r * Math.sin(startA);
    const x2 = cx + r * Math.cos(endA),   y2 = cy + r * Math.sin(endA);
    return { d: `M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`, ...sl };
  });

  const hov = hovered !== null ? paths[hovered] : null;
  const hovPct = hov ? Math.round((hov.value / total) * 100) : null;

  const svgEl = (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      {paths.map((p, i) => (
        <path key={i} d={p.d} fill="none" stroke={p.color}
          strokeWidth={hovered === i ? sw + size * 0.025 : sw} strokeLinecap="butt"
          style={{ opacity: hovered !== null && hovered !== i ? 0.35 : 1, cursor: 'pointer', transition: 'stroke-width 0.15s, opacity 0.15s' }}
          onMouseEnter={e => handleEnter(i, e)} onMouseMove={handleMove} onMouseLeave={handleLeave} />
      ))}
      {hov ? (
        <>
          <text x={cx} y={cy - fs * 0.3} textAnchor="middle" fontSize={fs * 1.15} fontWeight="700" fill={hov.color}>{hov.value}</text>
          <text x={cx} y={cy + fs * 1.1} textAnchor="middle" fontSize={Math.round(fs * 0.75)} fill="#94a3b8">{hovPct}%</text>
        </>
      ) : (
        <text x={cx} y={cy + fs * 0.35} textAnchor="middle" fontSize={fs} fontWeight="700" fill="#1e293b">{total}</text>
      )}
    </svg>
  );

  const tooltipCard = hov && (
    <div className={styles.donutTooltip} style={{ position: 'fixed', top: mousePos.y - 10, left: mousePos.x + 14, transform: 'translateY(-100%)' }}>
      <div className={styles.donutTooltipHeader}>
        <span className={styles.donutTooltipDot} style={{ background: hov.color }} />
        <span className={styles.donutTooltipLabel}>{hov.label}</span>
      </div>
      <div className={styles.donutTooltipNum}>{hov.value}</div>
      <div className={styles.donutTooltipPct}>{hovPct}% of total</div>
    </div>
  );

  if (hideLegend) return <div className={styles.donutSvgWrap}>{svgEl}{tooltipCard}</div>;

  return (
    <div className={styles.donutWrap}>
      {svgEl}
      <div className={styles.donutLegend}>
        {slices.map((sl, i) => (
          <div key={i} className={`${styles.legendItem} ${hovered === i ? styles.legendItemActive : ''}`}
            onMouseEnter={e => handleEnter(i, e)} onMouseMove={handleMove} onMouseLeave={handleLeave}>
            <span className={styles.legendDot} style={{ background: sl.color }} />
            <span className={styles.legendLabel}>{sl.label}</span>
            <span className={styles.legendVal}>{sl.value}</span>
          </div>
        ))}
      </div>
      {tooltipCard}
    </div>
  );
}

// ─── Bar Chart ─────────────────────────────────────────────────────────────────

function BarChart({ data, labelKey, valueKey, color = '#8b5cf6', maxBars = 12 }) {
  if (!data || !data.length) return <div className={styles.emptyChart}>No data</div>;
  const items = data.slice(0, maxBars);
  const max = Math.max(...items.map(d => d[valueKey]), 1);
  return (
    <div className={styles.barChart}>
      {items.map((d, i) => (
        <div key={i} className={styles.barRow}>
          <span className={styles.barLabel}>{d[labelKey]}</span>
          <div className={styles.barTrack}>
            <div className={styles.barFill} style={{ width: `${(d[valueKey] / max) * 100}%`, background: color }} />
          </div>
          <span className={styles.barVal}>{d[valueKey]}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Daily Line+Bar Chart ──────────────────────────────────────────────────────

function DailyChart({ data }) {
  const [hovIdx, setHovIdx] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const svgRef = useRef(null);

  if (!data || !data.length) return <div className={styles.emptyChart}>No data</div>;

  const maxOrders = Math.max(...data.map(d => d.orders), 1);
  const maxRev    = Math.max(...data.map(d => d.revenue), 1);
  const W = 760, H = 200, PAD = 40;
  const n = data.length;
  const xStep = (W - PAD * 2) / Math.max(n - 1, 1);
  const points = data.map((d, i) => ({ x: PAD + i * xStep, y: H - PAD - ((d.revenue / maxRev) * (H - PAD * 2)), ...d }));
  const polyline = points.map(p => `${p.x},${p.y}`).join(' ');
  const everyN = Math.ceil(n / 6);
  const hov = hovIdx !== null ? points[hovIdx] : null;

  function handleMouseMove(e) {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * W;
    const idx = Math.round((svgX - PAD) / xStep);
    setHovIdx(Math.max(0, Math.min(n - 1, idx)));
    setMousePos({ x: e.clientX, y: e.clientY });
  }

  return (
    <div className={styles.dailyChartWrap}>
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" height="200" style={{ cursor: 'default' }} onMouseMove={handleMouseMove} onMouseLeave={() => setHovIdx(null)}>
        {data.map((d, i) => {
          const x = PAD + i * xStep;
          const barH = (d.orders / maxOrders) * (H - PAD * 2);
          const barW = Math.max(xStep * 0.6, 3);
          return <rect key={i} x={x - barW/2} y={H - PAD - barH} width={barW} height={barH} fill={hovIdx === i ? '#cbd5e1' : '#e2e8f0'} rx="2" />;
        })}
        <polyline points={polyline} fill="none" stroke="#0d9488" strokeWidth="2.5" strokeLinejoin="round" />
        {hov && <line x1={hov.x} y1={PAD} x2={hov.x} y2={H - PAD} stroke="#0d9488" strokeWidth="1.5" strokeDasharray="4 3" opacity="0.6" />}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={hovIdx === i ? 6 : 3.5} fill={hovIdx === i ? '#fff' : '#0d9488'} stroke="#0d9488" strokeWidth={hovIdx === i ? 2.5 : 0} style={{ transition: 'r 0.1s' }} />
        ))}
        {data.map((d, i) => i % everyN === 0 && (
          <text key={i} x={PAD + i * xStep} y={H - 6} textAnchor="middle" fontSize="10" fill="#94a3b8">{d.date.slice(5)}</text>
        ))}
      </svg>
      {hov && (
        <div className={styles.chartTooltipCard} style={{ position: 'fixed', top: mousePos.y - 10, left: mousePos.x + 16, transform: 'translateY(-100%)' }}>
          <div className={styles.ctDate}>{hov.date}</div>
          <div className={styles.ctRow}><span className={styles.ctDot} style={{ background: '#94a3b8' }} /><span className={styles.ctLabel}>Orders</span><span className={styles.ctVal}>{hov.orders}</span></div>
          <div className={styles.ctRow}><span className={styles.ctDot} style={{ background: '#0d9488' }} /><span className={styles.ctLabel}>Revenue</span><span className={styles.ctVal}>{formatINR(hov.revenue)}</span></div>
        </div>
      )}
      <div className={styles.dailyLegend}>
        <span className={styles.legendItem}><span className={styles.legendBar} />Orders</span>
        <span className={styles.legendItem}><span className={styles.legendLine} style={{ background: '#0d9488' }} />Revenue (₹)</span>
      </div>
    </div>
  );
}

// ─── Status colors ────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  delivered:'#22c55e', in_transit:'#6366f1', confirmed:'#0ea5e9',
  ready_to_ship:'#a855f7', new:'#64748b', cancelled:'#ef4444',
  rto:'#f97316', held:'#eab308', label_generated:'#06b6d4',
  exceptions:'#f43f5e', lost:'#dc2626', damaged:'#7f1d1d',
};
const STATUS_LABELS = {
  delivered:'Delivered', in_transit:'In Transit', confirmed:'Confirmed',
  ready_to_ship:'Ready to Ship', new:'New', cancelled:'Cancelled',
  rto:'RTO', held:'Held', label_generated:'Label Generated',
  exceptions:'Exceptions', lost:'Lost', damaged:'Damaged',
};

// ─── Generic data-fetch hook ──────────────────────────────────────────────────

function useTabData(endpoint, startDate, endDate) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async (sd, ed) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (sd) params.set('start_date', sd);
      if (ed) params.set('end_date', ed);
      const res = await resilientFetch(
        `${BASE_URL}/api/dashboard/${endpoint}?${params}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        { timeoutMs: TIMEOUT_NORMAL, fetchImpl: (u, o) => fetch(u, o) }
      );
      if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        err.status = res.status;
        throw err;
      }
      setData(await res.json());
    } catch (e) {
      // Was `e.message`, which put "HTTP 502" on the dashboard. The classified
      // description names a cause instead; previously-loaded figures stay on
      // screen, so a failed refresh does not blank every tile.
      setError(e && e.classified ? e.classified : classifyError(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(startDate, endDate); }, [load, startDate, endDate]);

  return { data, loading, error, reload: () => load(startDate, endDate) };
}

// ─── Tab 1: Overview ──────────────────────────────────────────────────────────

function OverviewTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/', startDate, endDate);

  const ov  = data?.overview  || {};

  const statusSlices = Object.entries(data?.status_breakdown || {})
    .map(([key, val]) => ({ label: STATUS_LABELS[key] || key, value: val, color: STATUS_COLORS[key] || '#94a3b8' }))
    .filter(s => s.value > 0).sort((a, b) => b.value - a.value);

  const codPct = ov.cod_count && ov.total_orders ? Math.round((ov.cod_count / ov.total_orders) * 100) : 0;
  const prepaidPct = 100 - codPct;
  const codSlices = [
    { label: `COD (${codPct}%)`,     value: ov.cod_count     || 0, color: '#f97316' },
    { label: `Prepaid (${prepaidPct}%)`, value: ov.prepaid_count || 0, color: '#22c55e' },
  ];

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(s,e) => { setStartDate(s); setEndDate(e); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => {
          const ov = data?.overview || {};
          const vendorRows = (data?.vendor_performance || []).map(v => ({ vendor: v.vendor, orders: v.orders, revenue: v.revenue, delivered: v.delivered, rto: v.rto, cancelled: v.cancelled, cancel_rate: v.cancel_rate + '%', delivery_rate: v.delivery_rate + '%' }));
          const kpis = [{ label: 'Total Orders', value: ov.total_orders ?? 0 }, { label: 'Gross Sales', value: ov.gross_sales ?? 0 }, { label: 'Net Sales', value: ov.net_sales ?? 0 }, { label: 'Active Vendors', value: ov.active_vendors ?? 0 }, { label: 'RTO Orders', value: ov.rto_orders ?? 0 }, { label: 'Cancelled Orders', value: ov.cancelled_orders ?? 0 }, { label: 'Avg Order Value', value: ov.avg_order_value ?? 0 }, { label: 'Delivery Rate', value: (ov.delivery_rate ?? 0) + '%' }];
          exportCSV(`overview_${startDate}_${endDate}.csv`, vendorRows, kpis);
        }} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          <div className={styles.kpiGrid}>
            <StatCard label="TOTAL ORDERS"       value={ov.total_orders ?? '—'}           sub="Unique Shopify orders" />
            {userRole === 'admin' && <StatCard label="GROSS SALES"          value={formatINR(ov.gross_sales)}        sub="Before returns & discounts" />}
            {userRole === 'admin' && <StatCard label="NET SALES"            value={formatINR(ov.net_sales)}          sub="After returns & discounts" />}
            {userRole === 'admin' && <StatCard label="COMMISSION REVENUE"   value={formatINR(ov.commission_revenue)} sub={`${ov.avg_commission_pct ?? 0}% of Net Sales`} />}
            {userRole === 'admin' && <StatCard label="ACTIVE VENDORS"       value={ov.active_vendors ?? '—'}         sub="Unique brands" />}
            <StatCard label="AVG ORDER VALUE"     value={formatINR(ov.avg_order_value)}    sub="Per Shopify order" />
            <StatCard label="RTO"                  value={ov.rto_orders ?? '—'}             sub="Return to origin orders" />
            <StatCard label="DELIVERY RATE"       value={`${ov.delivery_rate ?? 0}%`}     sub={`${ov.delivered_orders ?? 0} orders`} />
            <StatCard label="CANCELLED"           value={ov.cancelled_orders ?? '—'}      sub="Cancelled orders" />
            {userRole === 'admin' && <StatCard label="AVG COMMISSION %"    value={`${ov.avg_commission_pct ?? 0}%`} sub="Weighted across orders" accent />}
          </div>

          {userRole === 'admin' && (
            <div className={styles.twoColSection} style={{ gridTemplateColumns: '320px 1fr' }}>
              <div className={styles.sectionCard}>
                <SectionTitle>Total Sales Breakdown</SectionTitle>
                <p className={styles.sectionSub}>Shopify-style revenue waterfall</p>
                <div className={styles.waterfall}>
                  <WaterfallRow label="Gross sales"               value={ov.gross_sales} />
                  <WaterfallRow label="Discounts"                 value={ov.total_discount}     sign="-" />
                  <WaterfallRow label="Returns (RTO + Cancelled)" value={(ov.returns_rto || 0) + (ov.cancelled_returns || 0)} sign="-" />
                  <div className={styles.waterfallDivider} />
                  <WaterfallRow label="Net sales"                 value={ov.net_sales} />
                  <WaterfallRow label="Taxes"                     value={ov.taxes} />
                  <div className={styles.waterfallDivider} />
                  <WaterfallRow label="Total sales"               value={ov.total_sales} />
                </div>
              </div>
              <div className={styles.sectionCard}>
                <SectionTitle>Total Sales over Time</SectionTitle>
                <div style={{ fontSize:'1.75rem', fontWeight:700, color:'#1e293b', marginBottom:'0.25rem' }}>{formatINR(ov.total_sales)}</div>
                <p className={styles.sectionSub}>
                  {ov.total_orders} orders
                  {data?.peak_day && ` · Peak: ${data.peak_day.date} (${data.peak_day.orders} orders)`}
                  {data?.avg_per_day != null && ` · Avg/day: ${data.avg_per_day}`}
                </p>
                <DailyChart data={data?.daily_orders || []} />
              </div>
            </div>
          )}

          {userRole !== 'admin' && (
            <div className={styles.sectionCard} style={{ marginBottom: '1.5rem' }}>
              <SectionTitle>Total Sales over Time</SectionTitle>
              <p className={styles.sectionSub}>{ov.total_orders} orders{data?.peak_day && ` · Peak: ${data.peak_day.date}`}</p>
              <DailyChart data={data?.daily_orders || []} />
            </div>
          )}

          <div className={styles.twoColSection}>
            <div className={styles.chartCard}>
              <h2 className={styles.chartCardTitle}>Order Status Breakdown</h2>
              <div className={styles.chartCardBody}><DonutChart slices={statusSlices} size={200} /></div>
            </div>
            <div className={styles.chartCard}>
              <h2 className={styles.chartCardTitle}>Payment Type</h2>
              <div className={styles.chartCardBody}>
                <DonutChart slices={codSlices} size={190} hideLegend />
                <div className={styles.codStats}>
                  <div className={styles.codStatItem} style={{ borderLeft: `3px solid #f97316` }}>
                    <div className={styles.codStatLeft}><span className={styles.codStatNum}>{ov.cod_count ?? 0}</span><span className={styles.codStatLabel}>COD orders</span></div>
                    <span className={styles.codStatPct} style={{ color: '#f97316' }}>{codPct}%</span>
                  </div>
                  <div className={styles.codStatItem} style={{ borderLeft: `3px solid #22c55e` }}>
                    <div className={styles.codStatLeft}><span className={styles.codStatNum}>{ov.prepaid_count ?? 0}</span><span className={styles.codStatLabel}>Prepaid orders</span></div>
                    <span className={styles.codStatPct} style={{ color: '#22c55e' }}>{prepaidPct}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {userRole === 'admin' && (
            <div className={styles.section}>
              <SectionTitle>Top 10 Vendors by Orders</SectionTitle>
              <p className={styles.sectionSub}>Ranked by unique Shopify order count</p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead><tr><th>#</th><th>Vendor</th><th>Orders</th><th>Delivered</th><th>Pending</th><th>RTO</th><th>Cancelled</th><th>Cancel Rate</th></tr></thead>
                  <tbody>
                    {(data?.top_vendors_by_orders || []).map((v, i) => (
                      <tr key={i}><td>{i+1}</td><td className={styles.vendorName}>{v.vendor}</td><td>{v.orders}</td>
                        <td className={styles.green}>{v.delivered}</td><td>{v.pending}</td>
                        <td className={styles.orange}>{v.rto}</td><td className={styles.red}>{v.cancelled}</td><td>{v.cancel_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {userRole === 'admin' && (
            <div className={styles.section}>
              <SectionTitle>Top 10 Vendors by Revenue</SectionTitle>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead><tr><th>#</th><th>Vendor</th><th>Orders</th><th>Revenue</th><th>Delivered</th><th>Cancelled</th><th>Cancel Rate</th></tr></thead>
                  <tbody>
                    {(data?.top_vendors_by_revenue || []).map((v, i) => (
                      <tr key={i}><td>{i+1}</td><td className={styles.vendorName}>{v.vendor}</td><td>{v.orders}</td>
                        <td className={styles.green}>{formatINR(v.revenue)}</td><td className={styles.green}>{v.delivered}</td>
                        <td className={styles.red}>{v.cancelled}</td><td>{v.cancel_rate}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </>
      )}
    </>
  );
}

// ─── Tab 2: Performance Report ────────────────────────────────────────────────

const STATUS_ROW_ITEMS = [
  { key: 'delivered',    label: 'Delivered',     color: '#22c55e' },
  { key: 'in_transit',   label: 'In Transit',    color: '#6366f1' },
  { key: 'confirmed',    label: 'Confirmed',     color: '#0ea5e9' },
  { key: 'ready_to_ship',label: 'Ready to Ship', color: '#a855f7' },
  { key: 'rto_count',    label: 'RTO',           color: '#f97316' },
  { key: 'cancelled_count', label: 'Cancelled',  color: '#ef4444' },
];

function PerformanceTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/performance/', startDate, endDate);
  const s = data?.summary || {};

  const PAGE_SIZE = 25;
  const { paged: ordersPage, page: ordPage, setPage: setOrdPage, totalPages: ordTotal } = usePagination(data?.orders, PAGE_SIZE);

  const statusSlices = Object.entries(data?.status_breakdown || {})
    .map(([key, val]) => ({ label: STATUS_LABELS[key] || key, value: val, color: STATUS_COLORS[key] || '#94a3b8' }))
    .filter(s => s.value > 0).sort((a, b) => b.value - a.value);

  const normalizePaymentType = (payment) => {
    if (!payment && payment !== 0) return '—';
    const raw = String(payment).trim().toLowerCase();
    if (raw === 'cod' || raw === 'cash_on_delivery' || raw === 'cashondelivery' || raw === 'pending' || raw.includes('cod')) return 'COD';
    if (raw === 'prepaid' || raw === 'paid' || raw.includes('prepaid')) return 'Prepaid';
    return String(payment)
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const codPct     = s.total_orders ? Math.round((s.cod_count    || 0) / s.total_orders * 100) : 0;
  const prepaidPct = s.total_orders ? Math.round((s.prepaid_count|| 0) / s.total_orders * 100) : 0;
  const codSlices = [
    { label: `COD (${codPct}%)`,     value: s.cod_count     || 0, color: '#f97316' },
    { label: `Prepaid (${prepaidPct}%)`, value: s.prepaid_count || 0, color: '#22c55e' },
  ].filter(sl => sl.value > 0);

  const periodLabel = startDate === endDate ? startDate : `${startDate} → ${endDate}`;

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`performance_orders_${startDate}_${endDate}.csv`, (data?.orders || []).map(o => ({ order_number: o.order_number, vendor: o.vendor, items: o.items, value: o.value, payment: normalizePaymentType(o.payment), status: o.status, created_at: o.created_at })), [{ label: 'Total Orders', value: s.total_orders ?? 0 }, { label: 'Gross Sales', value: s.gross_sales ?? 0 }, { label: 'Net Sales', value: s.net_sales ?? 0 }, { label: 'COD Orders', value: s.cod_count ?? 0 }, { label: 'Prepaid Orders', value: s.prepaid_count ?? 0 }, { label: 'Avg Order Value', value: s.avg_order_value ?? 0 }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          {/* Period label */}
          <p className={styles.sectionSub} style={{ marginBottom: '1rem' }}>
            {periodLabel}
          </p>

          {/* KPI row */}
          <div className={styles.kpiGrid}>
            <StatCard label="ORDERS RECEIVED"  value={s.total_orders ?? '—'}       sub="Unique Shopify orders" />
            {userRole === 'admin' && <StatCard label="GROSS SALES"   value={formatINR(s.gross_sales)}   sub="Before returns" />}
            {userRole === 'admin' && <StatCard label="NET SALES"     value={formatINR(s.net_sales)}     sub="After returns & discounts" />}
            <StatCard label="AVG ORDER VALUE"   value={formatINR(s.avg_order_value)} sub="Per order" />
            {userRole === 'admin' && <StatCard label="RETURNS (RTO+CAN)" value={s.returns_total ?? 0}  sub={`RTO: ${s.rto_count ?? 0} · Can: ${s.cancelled_count ?? 0}`} />}
          </div>

          {/* Status counts row */}
          <div className={styles.statusRow}>
            {STATUS_ROW_ITEMS.map(item => {
              const count = s[item.key] ?? 0;
              const pct   = s.total_orders ? Math.round(count / s.total_orders * 100) : 0;
              return (
                <div key={item.key} className={styles.statusRowItem}>
                  <div className={styles.statusRowCount} style={{ color: item.color }}>{count}</div>
                  <div className={styles.statusRowLabel}>{item.label}</div>
                  <div className={styles.statusRowPct}>{pct}%</div>
                </div>
              );
            })}
          </div>

          {/* Donut charts */}
          <div className={styles.twoColSection}>
            <div className={styles.chartCard}>
              <h2 className={styles.chartCardTitle}>Order Status Distribution</h2>
              <div className={styles.chartCardBody}><DonutChart slices={statusSlices} size={200} /></div>
            </div>
            <div className={styles.chartCard}>
              <h2 className={styles.chartCardTitle}>Payment Type (COD vs Prepaid)</h2>
              <div className={styles.chartCardBody}>
                <div className={styles.codLegendRow}>
                  <span className={styles.codLegendDot} style={{ background: '#f97316' }} />
                  <span>COD {s.cod_count ?? 0} orders ({codPct}%)</span>
                  <span className={styles.codLegendDot} style={{ background: '#22c55e', marginLeft: '1rem' }} />
                  <span>Prepaid {s.prepaid_count ?? 0} orders ({prepaidPct}%)</span>
                </div>
                <DonutChart slices={codSlices} size={190} hideLegend />
              </div>
            </div>
          </div>

          {/* Orders table */}
          {(data.orders || []).length > 0 && (
            <div className={styles.section}>
              <SectionTitle>Orders by Shopify Order # — {startDate === endDate ? startDate : `${startDate} to ${endDate}`}</SectionTitle>
              <p className={styles.sectionSub}>{s.total_orders} orders · {(data.orders || []).reduce((a,o) => a + (o.items||0), 0)} line items</p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr><th>Shopify Order #</th><th>Vendor</th><th>Product(s)</th><th>Items</th><th>Order Value</th><th>Payment</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {ordersPage.map((o, i) => (
                      <tr key={i}>
                        <td><strong>{o.order_number}</strong></td>
                        <td className={styles.vendorName}>{o.vendor}</td>
                        <td className={styles.productCell}>{(o.products || []).join(', ') || '—'}</td>
                        <td>{o.items}</td>
                        <td>{formatINR(o.value)}</td>
                        <td>
                          <span className={normalizePaymentType(o.payment) === 'COD' ? styles.codBadge : styles.prepaidBadge}>
                            {normalizePaymentType(o.payment)}
                          </span>
                        </td>
                        <td style={{ textTransform: 'capitalize', color: STATUS_COLORS[o.status] || '#64748b' }}>{(o.status || '').replace(/_/g, ' ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar page={ordPage} totalPages={ordTotal} setPage={setOrdPage} total={data.orders.length} pageSize={PAGE_SIZE} />
            </div>
          )}

        </>
      )}
    </>
  );
}

// ─── Tab 3: Vendor Performance (admin only) ───────────────────────────────────

function HBarChart({ data, labelKey, valueKey, maxBars = 10 }) {
  if (!data || !data.length) return <div className={styles.emptyChart}>No data</div>;
  const items = data.slice(0, maxBars);
  const max = Math.max(...items.map(d => d[valueKey]), 1);

  // X-axis tick marks
  const ticks = 5;
  const tickStep = Math.ceil(max / ticks / 1000) * 1000 || 1000;
  const tickVals = Array.from({ length: ticks + 1 }, (_, i) => i * tickStep);

  return (
    <div className={styles.hBarChartWrap}>
      <div className={styles.hBarChart}>
        {items.map((d, i) => {
          const pct = (d[valueKey] / (tickVals[tickVals.length - 1] || max)) * 100;
          return (
            <div key={i} className={styles.hBarRow}>
              <span className={styles.hBarLabel}>{d[labelKey]}</span>
              <div className={styles.hBarTrackWrap}>
                <div className={styles.hBarTrack}>
                  <div className={styles.hBarFill} style={{ width: `${Math.min(pct, 100)}%`, background: d.color || '#0d9488' }} />
                </div>
                <span className={styles.hBarVal}>{Number(d[valueKey]).toLocaleString('en-IN')}</span>
              </div>
            </div>
          );
        })}
        {/* X-axis */}
        <div className={styles.hBarXAxis}>
          <span className={styles.hBarLabel} />
          <div className={styles.hBarTrackWrap}>
            <div className={styles.hBarXTicks}>
              {tickVals.map(t => (
                <span key={t} className={styles.hBarTick}>
                  {t >= 1000 ? `${t / 1000}K` : t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function VendorPerformanceTab({ userRole }) {
  const isAdmin = userRole === 'admin';
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/vendor-performance/', startDate, endDate);
  const PAGE_SIZE = 25;
  const { paged, page, setPage, totalPages } = usePagination(data?.vendors, PAGE_SIZE);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`vendor_performance_${startDate}_${endDate}.csv`, (data?.vendors || []).map(v => ({ vendor: v.vendor, orders: v.orders, revenue: v.revenue, delivered: v.delivered, in_transit: v.in_transit, rto: v.rto, cancelled: v.cancelled, cancel_rate: v.cancel_rate + '%', avg_processing_days: v.avg_processing_days ?? 0 })), [{ label: 'Total Vendors', value: data?.active_vendors ?? 0 }, { label: 'Top Vendor', value: data?.top_vendor?.vendor ?? '—' }, { label: 'Avg Rev Per Vendor', value: data?.avg_rev_per_vendor ?? 0 }, { label: 'Avg Processing Days', value: data?.avg_processing_days ?? 0 }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          {/* KPI cards — admin sees all-vendor stats, vendor sees their own */}
          <div className={styles.kpiGrid}>
            {isAdmin && <StatCard label="TOTAL VENDORS"    value={data.active_vendors ?? '—'}         sub="Approved vendors" />}
            {isAdmin && <StatCard label="TOP VENDOR"       value={data.top_vendor?.vendor ?? '—'}     sub={data.top_vendor ? formatINR(data.top_vendor.revenue) : ''} />}
            {isAdmin && <StatCard label="AVG REV / VENDOR" value={formatINR(data.avg_rev_per_vendor)} sub="Across vendors with orders" />}
            <StatCard label="AVG PROCESSING TIME" value={data.avg_processing_days != null ? `${data.avg_processing_days} d` : '—'} sub="Created → Delivered" accent />
          </div>

          {/* Top vendors bar chart — admin only */}
          {isAdmin && (
            <div className={styles.sectionCard} style={{ marginBottom: '1.5rem' }}>
              <SectionTitle>Top Vendors by Revenue</SectionTitle>
              <HBarChart data={data.top10_chart || []} labelKey="vendor" valueKey="revenue" pageSize={10} />
            </div>
          )}

          {/* Full vendor table */}
          <div className={styles.section}>
            <SectionTitle>Vendor Report</SectionTitle>
            <p className={styles.sectionSub}>Performance by vendor · {(data.vendors || []).length} vendors</p>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>#</th><th>VENDOR</th><th>ORDERS</th><th>REVENUE</th>
                    <th>AVG PROCESSING</th><th>DELIVERED</th><th>IN TRANSIT</th>
                    <th>RTO</th><th>CANCELLED</th><th>CANCEL RATE</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((v, i) => (
                    <tr key={i}>
                      <td>{(page-1)*PAGE_SIZE+i+1}</td>
                      <td>{v.vendor}</td>
                      <td>{v.orders}</td>
                      <td className={styles.green}>{formatINR(v.revenue)}</td>
                      <td>
                        {v.avg_processing_days != null
                          ? <span className={v.avg_processing_days <= 3 ? styles.green : v.avg_processing_days <= 7 ? styles.orange : styles.red}>
                              {v.avg_processing_days} d
                              {v.processing_count ? <span style={{ color:'#94a3b8', fontWeight:400 }}> ({v.processing_count})</span> : null}
                            </span>
                          : <span style={{ color:'#94a3b8' }}>— (0)</span>
                        }
                      </td>
                      <td className={styles.green}>{v.delivered}</td>
                      <td style={{ color: '#6366f1', fontWeight: 600 }}>{v.in_transit}</td>
                      <td className={styles.orange}>{v.rto}</td>
                      <td className={styles.red}>{v.cancelled}</td>
                      <td>{v.cancel_rate}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} setPage={setPage} total={(data.vendors||[]).length} pageSize={PAGE_SIZE} />
          </div>
        </>
      )}
    </>
  );
}

// ─── Tab 4: Product Performance ───────────────────────────────────────────────

function ProductPerformanceTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/product-performance/', startDate, endDate);
  const s = data?.summary || {};
  const PAGE_SIZE = 25;
  const { paged, page, setPage, totalPages } = usePagination(data?.sku_breakdown, PAGE_SIZE);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`product_performance_${startDate}_${endDate}.csv`, (data?.sku_breakdown || []).map(p => ({ name: p.name, sku: p.sku || '', orders: p.orders, qty: p.qty, revenue: p.revenue })), [{ label: 'Unique Products', value: s.total_unique_products ?? 0 }, { label: 'Total Units Sold', value: s.total_qty_ordered ?? 0 }, { label: 'Top Product', value: data?.sku_breakdown?.[0]?.name ?? '—' }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          <div className={styles.kpiGrid}>
            <StatCard label="UNIQUE PRODUCTS" value={s.total_unique_products ?? '—'} sub="In selected period" />
            <StatCard label="TOTAL UNITS SOLD" value={s.total_qty_ordered ?? '—'}    sub="Units ordered" />
            <StatCard label="TOP PRODUCT"      value={data.sku_breakdown?.[0]?.name ?? '—'} sub={data.sku_breakdown?.[0] ? `${data.sku_breakdown[0].orders} orders` : ''} />
          </div>

          <div className={styles.section}>
            <SectionTitle>Product Report</SectionTitle>
            <p className={styles.sectionSub}>Top products and SKU analytics · {(data.sku_breakdown || []).length} products</p>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Shopify Orders</th>
                    <th>Units</th>
                    {userRole === 'admin' && <th>Revenue</th>}
                  </tr>
                </thead>
                <tbody>
                  {paged.map((p, i) => (
                    <tr key={i}>
                      <td>{(page - 1) * PAGE_SIZE + i + 1}</td>
                      <td style={{ maxWidth: 320 }}>{p.name}</td>
                      <td><code>{p.sku || '—'}</code></td>
                      <td>{p.orders}</td>
                      <td>{p.qty}</td>
                      {userRole === 'admin' && <td className={styles.green}>{formatINR(p.revenue)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={page} totalPages={totalPages} setPage={setPage} total={(data.sku_breakdown || []).length} pageSize={PAGE_SIZE} />
          </div>
        </>
      )}
    </>
  );
}

// ─── Tab 5: Shipping Performance ──────────────────────────────────────────────

function ShippingPerformanceTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/shipping-performance/', startDate, endDate);
  const s = data?.summary || {};
  const PAGE_SIZE = 25;
  const { paged, page, setPage, totalPages } = usePagination(data?.inflight_orders, PAGE_SIZE);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`shipping_performance_${startDate}_${endDate}.csv`, (data?.inflight_orders || []).map(o => ({ date: o.date, order_number: o.order_number, vendor: o.vendor, awb: o.awb, partner: o.partner, status: o.status })), [{ label: 'Total Shipments', value: s.total_shipments ?? 0 }, { label: 'Pending', value: s.pending ?? 0 }, { label: 'RTO', value: s.rto ?? 0 }, { label: 'AWB Assigned', value: s.awb_assigned ?? 0 }, { label: 'Pending %', value: (s.pending_pct ?? 0) + '%' }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          {/* KPI row matching PDF */}
          <div className={styles.kpiGrid}>
            <StatCard label="TOTAL SHIPMENTS"  value={s.total_shipments ?? '—'} sub="In period" />
            <StatCard label="PENDING"          value={s.pending ?? '—'}         sub={`${s.pending_pct ?? 0}%`} />
            <StatCard label="RTO"              value={s.rto ?? '—'}             sub="Return to origin" />
            {userRole === 'admin' && <StatCard label="PARTNERS (NAMED)" value={s.partners_named ?? '—'} sub="Blank rows excluded" />}
            <StatCard label="AWB ASSIGNED"     value={s.awb_assigned ?? '—'}    sub="Shipments with AWB" accent />
          </div>

          {/* Two-column: partners bar chart + status donut */}
          <div className={styles.twoColSection}>
            <div className={styles.sectionCard}>
              <SectionTitle>Shipping Partners</SectionTitle>
              <HBarChart data={data.partners_chart || []} labelKey="partner" valueKey="count" maxBars={12} />
            </div>
            <div className={styles.sectionCard}>
              <SectionTitle>Shipping Status</SectionTitle>
              <DonutChart slices={(data.status_donut || []).filter(s => s.value > 0)} size={200} />
            </div>
          </div>

          {/* In-flight orders table */}
          {(data.inflight_orders || []).length > 0 && (
            <div className={styles.section}>
              <SectionTitle>In-Flight Orders</SectionTitle>
              <p className={styles.sectionSub}>
                {(data.inflight_orders || []).filter(o => o.awb !== '—').length} orders with AWB &amp; Partner assigned
                &nbsp;·&nbsp;
                {(data.inflight_orders || []).filter(o => o.awb === '—').length} pending AWB assignment
              </p>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr><th>Date</th><th>Shopify #</th><th>Vendor</th><th>AWB</th><th>Partner</th><th>Tracking</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {paged.map((o, i) => (
                      <tr key={i}>
                        <td>{o.date}</td>
                        <td><strong>#{o.order_number}</strong></td>
                        <td className={styles.vendorName}>{o.vendor}</td>
                        <td><code style={{ fontSize: '0.78rem' }}>{o.awb}</code></td>
                        <td>{o.partner}</td>
                        <TrackingCell url={o.tracking_link} />
                        <td style={{ color: STATUS_COLORS[o.status] || '#64748b', fontWeight: 600, textTransform: 'capitalize' }}>
                          {(o.status || '').replace(/_/g, ' ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar page={page} totalPages={totalPages} setPage={setPage} total={(data.inflight_orders||[]).length} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </>
  );
}

// ─── Tab 6: Geographic Performance ───────────────────────────────────────────

function GeographicPerformanceTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/geographic/', startDate, endDate);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`geographic_${startDate}_${endDate}.csv`, (data?.states || []).map(s => ({ rank: s.rank, state: s.state, orders: s.orders, revenue: s.revenue, pct: s.pct + '%' })), [{ label: 'Total States', value: data?.total_states ?? 0 }, { label: 'Total Cities', value: data?.total_cities ?? 0 }, { label: 'Top State', value: data?.top_state?.state ?? '—' }, { label: 'Top City', value: data?.top_city?.city ?? '—' }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          <div className={styles.kpiGrid}>
            <StatCard label="TOTAL STATES" value={data.total_states ?? '—'} sub="Delivering to" />
            <StatCard label="TOTAL CITIES" value={data.total_cities ?? '—'} sub="Delivering to" />
            <StatCard label="TOP STATE"    value={data.top_state?.state ?? '—'} sub={data.top_state ? `${data.top_state.orders} orders` : ''} />
            <StatCard label="TOP CITY"     value={data.top_city?.city ?? '—'}   sub={data.top_city ? `${data.top_city.orders} orders` : ''} />
          </div>

          <div className={styles.twoColSection}>
            <div className={styles.sectionCard}><SectionTitle>Top States by Orders</SectionTitle><BarChart data={data.states || []} labelKey="state" valueKey="orders" color="#8b5cf6" /></div>
            <div className={styles.sectionCard}><SectionTitle>Top Cities by Orders</SectionTitle><BarChart data={data.cities || []} labelKey="city" valueKey="orders" color="#0d9488" maxBars={15} /></div>
          </div>

          <div className={styles.section}>
            <SectionTitle>State-wise Breakdown</SectionTitle>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr><th>#</th><th>State</th><th>Orders</th>{userRole === 'admin' && <th>Revenue</th>}<th>% of Orders</th></tr></thead>
                <tbody>
                  {(data.states || []).map(s => (
                    <tr key={s.rank}><td>{s.rank}</td><td className={styles.vendorName}>{s.state}</td><td>{s.orders}</td>
                      {userRole === 'admin' && <td className={styles.green}>{formatINR(s.revenue)}</td>}<td>{s.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.section}>
            <SectionTitle>City-wise Breakdown</SectionTitle>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead><tr><th>#</th><th>City</th><th>State</th><th>Orders</th>{userRole === 'admin' && <th>Revenue</th>}</tr></thead>
                <tbody>
                  {(data.cities || []).map((c, i) => (
                    <tr key={i}><td>{i+1}</td><td className={styles.vendorName}>{c.city}</td><td>{c.state}</td><td>{c.orders}</td>
                      {userRole === 'admin' && <td className={styles.green}>{formatINR(c.revenue)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

// ─── Tab 7: Order Ageing ──────────────────────────────────────────────────────

const STATUS_TAG_COLORS = {
  new: '#64748b', confirmed: '#0ea5e9', cancelled: '#ef4444',
  ready_to_ship: '#a855f7', in_transit: '#6366f1', delivered: '#22c55e',
  rto: '#f97316', held: '#eab308', label_generated: '#06b6d4',
  exceptions: '#f43f5e', lost: '#dc2626', damaged: '#7f1d1d',
};

function StatusTag({ status }) {
  const color = STATUS_TAG_COLORS[status] || '#64748b';
  const label = STATUS_LABELS[status] || status;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      background: color + '18', color, fontSize: '0.72rem', fontWeight: 600,
      marginRight: 4, marginBottom: 2, textTransform: 'capitalize',
    }}>
      {label}
    </span>
  );
}

function OrderAgeingTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/order-ageing/', startDate, endDate);
  const s = data?.summary || {};
  const PAGE_SIZE = 25;
  const { paged, page, setPage, totalPages } = usePagination(data?.critical_orders, PAGE_SIZE);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`order_ageing_${startDate}_${endDate}.csv`, (data?.critical_orders || []).map(o => ({ created_at: o.created_at, order_number: o.order_number, vendor: o.vendor, status: o.status, age_days: o.age_days, value: o.value })), [{ label: 'Total Orders', value: s.total_orders ?? 0 }, { label: 'Avg Order Age (Days)', value: s.avg_age_days ?? 0 }, { label: 'Active Orders', value: s.active_orders ?? 0 }, { label: 'Critical Orders (>5 Days)', value: s.critical_count ?? 0 }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          {/* KPI row — matches PDF */}
          <div className={styles.kpiGrid}>
            <StatCard label="TOTAL ORDERS"    value={s.total_orders ?? '—'}    sub="All statuses" />
            <StatCard label="AVG ORDER AGE"   value={s.avg_age_days != null ? `${s.avg_age_days} days` : '—'} sub="From order date to today" accent />
            <StatCard label="ACTIVE ORDERS"   value={s.active_orders ?? '—'}   sub="Not delivered/cancelled" />
            <StatCard label="CRITICAL (>5 DAYS)" value={s.critical_count ?? '—'} sub="Requires escalation" />
          </div>

          {/* Order Ageing Breakdown table */}
          <div className={styles.sectionCard} style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <SectionTitle>Order Ageing Breakdown</SectionTitle>
              <span className={styles.sectionSub}>All orders bucketed by Order Ageing (Days) field</span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Ageing Bucket</th>
                    <th>Orders</th>
                    <th>Line Items</th>
                    <th>% of Total</th>
                    <th>Statuses</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.buckets || []).map((b, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{b.label}</td>
                      <td>{b.orders}</td>
                      <td>{b.line_items}</td>
                      <td>{b.pct}%</td>
                      <td>
                        {(b.statuses || []).map(st => <StatusTag key={st} status={st} />)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Critical orders */}
          {(data.critical_orders || []).length > 0 && (
            <div className={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <SectionTitle>🚨 Critical Orders — Order Age &gt; 5 Days</SectionTitle>
                <span className={styles.sectionSub}>{s.critical_count} orders need immediate attention</span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr><th>Order Date</th><th>Shopify #</th><th>Vendor</th><th>Status</th><th>Order Age</th><th>Order Value</th></tr>
                  </thead>
                  <tbody>
                    {paged.map((o, i) => (
                      <tr key={i}>
                        <td style={{ color: '#0ea5e9' }}>{o.created_at}</td>
                        <td><strong>#{o.order_number}</strong></td>
                        <td className={styles.vendorName}>{o.vendor}</td>
                        <td><StatusTag status={o.status} /></td>
                        <td className={o.age_days > 10 ? styles.red : styles.orange}><strong>{o.age_days}d</strong></td>
                        <td>{formatINR(o.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar page={page} totalPages={totalPages} setPage={setPage} total={(data.critical_orders||[]).length} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </>
  );
}

// ─── Tab 8: Shipment Ageing ───────────────────────────────────────────────────

function ShipmentAgeingTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/shipment-ageing/', startDate, endDate);
  const s = data?.summary || {};
  const PAGE_SIZE = 25;
  const { paged, page, setPage, totalPages } = usePagination(data?.critical_orders, PAGE_SIZE);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`shipment_ageing_${startDate}_${endDate}.csv`, (data?.critical_orders || []).map(o => ({ created_at: o.created_at, order_number: o.order_number, vendor: o.vendor, status: o.status, age_days: o.age_days, awb: o.awb, partner: o.partner })), [{ label: 'Active Shipments', value: s.active_shipments ?? 0 }, { label: 'Avg Shipment Age (Days)', value: s.avg_age_days ?? 0 }, { label: 'Critical Shipments (4+ Days)', value: s.critical_count ?? 0 }, { label: 'Ready to Ship', value: s.ready_to_ship ?? 0 }, { label: 'In Transit', value: s.in_transit ?? 0 }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          {/* KPI row — matches PDF */}
          <div className={styles.kpiGrid}>
            <StatCard label="ACTIVE SHIPMENTS"  value={s.active_shipments ?? '—'} sub="Ready to Ship + In Transit" />
            <StatCard label="AVG SHIPMENT AGE"  value={s.avg_age_days != null ? `${s.avg_age_days} days` : '—'} sub="From handover/AWB date" accent />
            <StatCard label="CRITICAL (4+ DAYS)" value={s.critical_count ?? '—'}  sub="Immediate escalation needed" />
            <StatCard label="READY TO SHIP"     value={s.ready_to_ship ?? '—'}    sub="Awaiting pickup" />
            <StatCard label="IN TRANSIT"        value={s.in_transit ?? '—'}       sub="Currently moving" />
          </div>

          {/* Shipment Ageing Breakdown table */}
          <div className={styles.sectionCard} style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <SectionTitle>Shipment Ageing Breakdown</SectionTitle>
              <span className={styles.sectionSub}>Shipment Ageing (Days) field — Ready to Ship &amp; In Transit orders only</span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Shipment Age</th>
                    <th>Orders</th>
                    <th>Line Items</th>
                    <th>% of Active</th>
                    <th>Action Required</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.buckets || []).map((b, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{b.label}</td>
                      <td style={{ color: b.is_critical ? '#ef4444' : '#0ea5e9', fontWeight: 700 }}>{b.orders}</td>
                      <td>{b.line_items}</td>
                      <td>{b.pct}%</td>
                      <td style={{ color: b.is_critical ? '#ef4444' : '#475569', fontWeight: b.is_critical ? 700 : 400 }}>
                        {b.action}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Critical shipments */}
          {(data.critical_orders || []).length > 0 && (
            <div className={styles.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <SectionTitle>🚨 Critical Shipments — 4+ Days Old</SectionTitle>
                <span className={styles.sectionSub}>{s.critical_count} shipments</span>
              </div>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr><th>Order Date</th><th>Shopify #</th><th>Vendor</th><th>Status</th><th>Shipment Age</th><th>AWB</th><th>Partner</th><th>Tracking</th></tr>
                  </thead>
                  <tbody>
                    {paged.map((o, i) => (
                      <tr key={i}>
                        <td style={{ color: '#0ea5e9' }}>{o.created_at}</td>
                        <td><strong>#{o.order_number}</strong></td>
                        <td className={styles.vendorName}>{o.vendor}</td>
                        <td><StatusTag status={o.status} /></td>
                        <td className={styles.red}><strong>{o.age_days}d</strong></td>
                        <td><code style={{ fontSize: '0.78rem' }}>{o.awb}</code></td>
                        <td>{o.partner}</td>
                        <TrackingCell url={o.tracking_link} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <PaginationBar page={page} totalPages={totalPages} setPage={setPage} total={(data.critical_orders||[]).length} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </>
  );
}

// ─── Tab 9: Delivered Ageing ──────────────────────────────────────────────────

function DeliveredAgeingTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/delivered-ageing/', startDate, endDate);
  const k = data?.kpis || {};
  const vendorPag = usePagination(data?.vendor_ageing, 25);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`delivered_ageing_${startDate}_${endDate}.csv`, (data?.vendor_ageing || []).map(v => ({ vendor: v.vendor, delivered_orders: v.delivered_orders, avg_ageing: v.avg_ageing })), [{ label: 'Delivered Orders', value: k.total_delivered ?? 0 }, { label: 'With Ageing Data', value: k.with_ageing_data ?? 0 }, { label: 'Avg Delivered Age (Days)', value: k.avg_delivered_age ?? 0 }, { label: 'Vendors', value: k.vendors ?? 0 }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          <div className={styles.kpiGrid}>
            <StatCard label="DELIVERED ORDERS"  value={k.total_delivered ?? '—'} sub="Successfully closed" />
            <StatCard label="WITH AGEING DATA"  value={k.with_ageing_data ?? '—'} sub="Line items with data" />
            <StatCard label="AVG DELIVERED AGE" value={k.avg_delivered_age != null ? `${k.avg_delivered_age} days` : '—'} sub="Days since delivery" accent />
            <StatCard label="VENDORS"           value={k.vendors ?? '—'} sub="Brands with deliveries" />
          </div>

          <div className={styles.section}>
            <SectionTitle>Delivered Ageing Breakdown</SectionTitle>
            <p className={styles.sectionSub}>Delivered Ageing (Days) — days since the order was marked delivered</p>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>AGEING BUCKET</th>
                    <th>ORDERS</th>
                    <th>LINE ITEMS</th>
                    <th>% OF DELIVERED</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.breakdown || []).map(b => (
                    <tr key={b.label}>
                      <td>{b.label}</td>
                      <td><strong style={{color:'#2563eb'}}>{b.orders}</strong></td>
                      <td>{b.line_items}</td>
                      <td>{b.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.section}>
            <SectionTitle>Avg Delivered Ageing by Vendor</SectionTitle>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr><th>#</th><th>VENDOR</th><th>DELIVERED ORDERS</th><th>AVG AGEING (DAYS)</th></tr>
                </thead>
                <tbody>
                  {vendorPag.paged.map((v, i) => (
                    <tr key={v.vendor}>
                      <td>{(vendorPag.page - 1) * 25 + i + 1}</td>
                      <td>{v.vendor}</td>
                      <td><strong style={{color:'#2563eb'}}>{v.delivered_orders}</strong></td>
                      <td>{v.avg_ageing}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={vendorPag.page} totalPages={vendorPag.totalPages} setPage={vendorPag.setPage} total={(data.vendor_ageing||[]).length} pageSize={25} />
          </div>
        </>
      )}
    </>
  );
}

// ─── Tab 10: Delivery Timeline ────────────────────────────────────────────────

function DeliveryTimelineTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/delivery-timeline/', startDate, endDate);
  const k = data?.kpis || {};
  const vendorPag = usePagination(data?.vendor_timeline, 25);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`delivery_timeline_${startDate}_${endDate}.csv`, (data?.vendor_timeline || []).map(v => ({ vendor: v.vendor, orders: v.orders, avg_days: v.avg_days, min_days: v.min_days, max_days: v.max_days })), [{ label: 'Delivered Orders', value: k.total_delivered ?? 0 }, { label: 'Avg Delivery Days', value: k.avg_delivery_days ?? 0 }, { label: 'Fastest Delivery (Days)', value: k.fastest_delivery ?? 0 }, { label: 'Slowest Delivery (Days)', value: k.slowest_delivery ?? 0 }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          <div className={styles.kpiGrid}>
            <StatCard label="DELIVERED ORDERS"   value={k.total_delivered ?? '—'}   sub="With delivery days data" />
            <StatCard label="AVG DELIVERY DAYS"  value={k.avg_delivery_days != null ? `${k.avg_delivery_days} days` : '—'} sub="Delivered orders only" accent />
            <StatCard label="FASTEST DELIVERY"   value={k.fastest_delivery != null ? `${k.fastest_delivery} day${k.fastest_delivery === 1 ? '' : 's'}` : '—'} sub="Minimum delivery time" />
            <StatCard label="SLOWEST DELIVERY"   value={k.slowest_delivery != null ? `${k.slowest_delivery} days` : '—'} sub="Maximum delivery time" />
          </div>

          <div className={styles.section}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
              <SectionTitle>Delivery Speed Breakdown</SectionTitle>
              <span className={styles.sectionSub}>Delivery Days field — delivered orders only</span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>DELIVERY TIME</th>
                    <th>ORDERS</th>
                    <th>% OF DELIVERED</th>
                    <th>PERFORMANCE</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.breakdown || []).map(b => (
                    <tr key={b.label}>
                      <td>{b.label}</td>
                      <td><strong style={{color:'#2563eb'}}>{b.orders}</strong></td>
                      <td>{b.pct}%</td>
                      <td>{b.performance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.section}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
              <SectionTitle>Delivery Performance by Vendor</SectionTitle>
              <span className={styles.sectionSub}>Sorted by avg delivery days (fastest first)</span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr><th>#</th><th>VENDOR</th><th>DELIVERED ORDERS</th><th>AVG DAYS</th><th>MIN DAYS</th><th>MAX DAYS</th></tr>
                </thead>
                <tbody>
                  {vendorPag.paged.map((v, i) => (
                    <tr key={v.vendor}>
                      <td>{(vendorPag.page - 1) * 25 + i + 1}</td>
                      <td>{v.vendor}</td>
                      <td>{v.orders}</td>
                      <td style={{color:'#16a34a', fontWeight:600}}>{v.avg_days}</td>
                      <td>{v.min_days}</td>
                      <td>{v.max_days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PaginationBar page={vendorPag.page} totalPages={vendorPag.totalPages} setPage={vendorPag.setPage} total={(data.vendor_timeline||[]).length} pageSize={25} />
          </div>
        </>
      )}
    </>
  );
}

// ─── Tab 11: Order Cancellation ───────────────────────────────────────────────

function CancelBarChart({ data }) {
  const max = Math.max(...data.map(d => d.count), 1);
  return (
    <div className={styles.cancelBarChart}>
      {data.map(d => (
        <div key={d.date} className={styles.cancelBarCol}>
          <div className={styles.cancelBarWrap}>
            <div className={styles.cancelBar} style={{ height: `${Math.round((d.count / max) * 100)}%` }} />
          </div>
          <div className={styles.cancelBarVal}>{d.count > 0 ? d.count : ''}</div>
          <div className={styles.cancelBarDate}>{d.date.slice(5)}</div>
        </div>
      ))}
    </div>
  );
}

function CancellationTab({ userRole }) {
  const defaultRange = getPresetRange('last30');
  const [startDate, setStartDate] = useState(toISODate(defaultRange.start));
  const [endDate,   setEndDate]   = useState(toISODate(defaultRange.end));
  const { data, loading, error, reload } = useTabData('terminal/cancellation/', startDate, endDate);
  const k = data?.kpis || {};
  const vendorPag = usePagination(data?.vendor_breakdown, 25);

  return (
    <>
      <TabHeader startDate={startDate} endDate={endDate} defaultRange={defaultRange}
        onDateChange={(sd,ed) => { setStartDate(sd); setEndDate(ed); }} onRefresh={reload}
        userRole={userRole}
        onExport={() => exportCSV(`cancellation_${startDate}_${endDate}.csv`, (data?.vendor_breakdown || []).map(v => ({ vendor: v.vendor, cancelled_orders: v.cancelled_orders, total_orders: v.total_orders, cancel_rate: v.cancel_rate + '%', revenue_lost: v.revenue_lost })), [{ label: 'Total Cancellations', value: k.total_cancellations ?? 0 }, { label: 'Cancel Rate', value: (k.cancel_rate ?? 0) + '%' }, { label: 'Revenue Lost', value: k.revenue_lost ?? 0 }, { label: 'COD Cancellations', value: k.cod_cancellations ?? 0 }, { label: 'Prepaid Cancellations', value: k.prepaid_cancellations ?? 0 }, { label: 'Vendors Affected', value: k.vendors_affected ?? 0 }])} />

      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} onRetry={reload} />}
      {!loading && !error && data && (
        <>
          <div className={styles.kpiGrid}>
            <StatCard label="TOTAL CANCELLATIONS" value={k.total_cancellations ?? '—'} sub={`${k.cancel_rate ?? 0}% cancel rate`} />
            {userRole === 'admin' && <StatCard label="REVENUE LOST"          value={formatINR(k.revenue_lost ?? 0)} sub="Cancelled order value" accent />}
            {userRole === 'admin' && <StatCard label="COD CANCELLATIONS"     value={k.cod_cancellations ?? '—'} sub={`${k.cod_pct ?? 0}% of cancels`} />}
            {userRole === 'admin' && <StatCard label="PREPAID CANCELLATIONS" value={k.prepaid_cancellations ?? '—'} sub={`${k.prepaid_pct ?? 0}% of cancels`} />}
            {userRole === 'admin' && <StatCard label="VENDORS AFFECTED"      value={k.vendors_affected ?? '—'} sub="Brands with cancellations" />}
          </div>

          <div className={styles.section}>
            <SectionTitle>Cancellation by Reason</SectionTitle>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr><th>REASON</th><th>ORDERS</th><th>LINE ITEMS</th><th>REVENUE LOST</th><th>% OF CANCELLATIONS</th></tr>
                </thead>
                <tbody>
                  {(data.reason_breakdown || []).map(r => (
                    <tr key={r.reason}>
                      <td>{r.reason}</td>
                      <td><strong style={{color:'#dc2626'}}>{r.orders}</strong></td>
                      <td>{r.line_items}</td>
                      <td>{formatINR(r.revenue)}</td>
                      <td>{r.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className={styles.section}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
              <SectionTitle>Cancellations by Vendor</SectionTitle>
              <span className={styles.sectionSub}>Vendors ranked by number of cancelled orders</span>
            </div>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr><th>#</th><th>VENDOR</th><th>CANCELLED ORDERS</th><th>TOTAL ORDERS</th><th>CANCEL RATE</th>{userRole === 'admin' && <th>REVENUE LOST</th>}</tr>
                </thead>
                <tbody>
                  {vendorPag.paged.map((v, i) => {
                    const rate = v.cancel_rate;
                    const rateColor = rate >= 100 ? '#dc2626' : rate >= 50 ? '#ea580c' : rate >= 20 ? '#ca8a04' : '#16a34a';
                    return (
                      <tr key={v.vendor}>
                        <td>{(vendorPag.page - 1) * 25 + i + 1}</td>
                        <td>{v.vendor}</td>
                        <td><strong style={{color:'#dc2626'}}>{v.cancelled_orders}</strong></td>
                        <td>{v.total_orders}</td>
                        <td style={{color: rateColor, fontWeight: 600}}>{rate}%</td>
                        {userRole === 'admin' && <td>{formatINR(v.revenue_lost)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationBar page={vendorPag.page} totalPages={vendorPag.totalPages} setPage={vendorPag.setPage} total={(data.vendor_breakdown||[]).length} pageSize={25} />
          </div>

          <div className={styles.section}>
            <SectionTitle>Daily Cancellation Trend — Last 14 Days</SectionTitle>
            <CancelBarChart data={data.daily_trend || []} />
          </div>
        </>
      )}
    </>
  );
}

// ─── Tab definitions ──────────────────────────────────────────────────────────

const ALL_TABS = [
  { key: 'overview',          label: 'Overview',               adminOnly: false },
  { key: 'performance',       label: 'Performance',            adminOnly: false },
  { key: 'vendor',            label: 'Vendor Performance',     adminOnly: true  },
  { key: 'product',           label: 'Product Performance',    adminOnly: false },
  { key: 'shipping',          label: 'Shipping Performance',   adminOnly: false },
  { key: 'geographic',        label: 'Geographic Performance', adminOnly: false },
  { key: 'order_ageing',      label: 'Order Ageing',           adminOnly: false },
  { key: 'shipment_ageing',   label: 'Shipment Ageing',        adminOnly: false },
  { key: 'delivered_ageing',  label: 'Delivered Ageing',       adminOnly: false },
  { key: 'delivery_timeline', label: 'Delivery Timeline',      adminOnly: false },
  { key: 'cancellation',      label: 'Cancellation',           adminOnly: false },
];

// ─── Main Dashboard Shell ──────────────────────────────────────────────────────

export default function TerminalDashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [userRole, setUserRole]   = useState(null);
  useTaraTerminalFocus('dashboard_tab', ALL_TABS.find(t => t.key === activeTab)?.label || activeTab);

  useEffect(() => {
    getCurrentUserRole()
      .then(r => setUserRole(r?.role))
      .catch(() => setUserRole(null));
  }, []);

  const visibleTabs = ALL_TABS.filter(t => !t.adminOnly || userRole === 'admin');

  return (
    <div className={styles.page}>
      <TerminalBannerManager />
      <div className={styles.tabBar}>
        {visibleTabs.map(t => (
          <button
            key={t.key}
            className={`${styles.tabBtn} ${activeTab === t.key ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview'          && <OverviewTab userRole={userRole} />}
      {activeTab === 'performance'       && <PerformanceTab userRole={userRole} />}
      {activeTab === 'vendor'            && userRole === 'admin' && <VendorPerformanceTab userRole={userRole} />}
      {activeTab === 'product'           && <ProductPerformanceTab userRole={userRole} />}
      {activeTab === 'shipping'          && <ShippingPerformanceTab userRole={userRole} />}
      {activeTab === 'geographic'        && <GeographicPerformanceTab userRole={userRole} />}
      {activeTab === 'order_ageing'      && <OrderAgeingTab userRole={userRole} />}
      {activeTab === 'shipment_ageing'   && <ShipmentAgeingTab userRole={userRole} />}
      {activeTab === 'delivered_ageing'  && <DeliveredAgeingTab userRole={userRole} />}
      {activeTab === 'delivery_timeline' && <DeliveryTimelineTab userRole={userRole} />}
      {activeTab === 'cancellation'      && <CancellationTab userRole={userRole} />}
    </div>
  );
}
